/**
 * HistoricoFichaDialog — lista snapshots da Ficha de Especificação.
 *
 * Exibe até 30 versões (rolling), permite VER (read-only) ou RESTAURAR.
 * Restauração só substitui os campos críticos (itens_custo, custo_manual, custo_total,
 * status, fornecedor_id) — não toca em foto, observação ou anexos.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { History, Eye, RotateCcw, Loader2, Package } from 'lucide-react';
import { formatCurrency } from './constantes';

const formatDateBR = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso || '';
  }
};

const motivoLabel = {
  'auto-save': { label: 'Auto-save', color: 'bg-gray-100 text-gray-700' },
  'save-manual': { label: 'Save manual', color: 'bg-blue-100 text-blue-700' },
  'restauracao': { label: 'Restauração', color: 'bg-amber-100 text-amber-700' },
  'pre-restauracao': { label: 'Pré-restauração', color: 'bg-purple-100 text-purple-700' },
};

const HistoricoFichaDialog = ({
  open,
  onClose,
  projectId,
  embarcacaoIndex,
  onRestoreSuccess,
}) => {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verSnapshot, setVerSnapshot] = useState(null);
  const [snapshotParaRestaurar, setSnapshotParaRestaurar] = useState(null);
  const [restaurando, setRestaurando] = useState(false);

  const fetchSnapshots = async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/projects/${projectId}/ficha-historico/${embarcacaoIndex}`
      );
      setSnapshots(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
      toast.error('Erro ao carregar histórico da ficha');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && projectId) {
      fetchSnapshots();
    }
  }, [open, projectId, embarcacaoIndex]);

  const handleRestaurar = async () => {
    if (!snapshotParaRestaurar) return;
    setRestaurando(true);
    try {
      const res = await api.post(
        `/projects/${projectId}/ficha-historico/${embarcacaoIndex}/restaurar/${snapshotParaRestaurar.id}`
      );
      toast.success(
        `Ficha restaurada: ${res.data?.itens_restaurados || 0} item(ns) atualizado(s)`,
        { duration: 6000 }
      );
      setSnapshotParaRestaurar(null);
      onClose?.();
      onRestoreSuccess?.();
    } catch (err) {
      console.error('Erro ao restaurar:', err);
      toast.error(err?.response?.data?.detail || 'Erro ao restaurar snapshot');
    } finally {
      setRestaurando(false);
    }
  };

  const itensVerSnapshot = useMemo(() => {
    if (!verSnapshot?.itens) return [];
    return verSnapshot.itens;
  }, [verSnapshot]);

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent
          className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col"
          onInteractOutside={(e) => e.preventDefault()}
          data-testid="historico-ficha-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-4 w-4 text-blue-600" />
              Histórico da Ficha de Especificação
            </DialogTitle>
            <DialogDescription>
              Mantemos as últimas 30 versões salvas (auto-save e save manual). Você
              pode visualizar uma versão anterior ou restaurá-la — a restauração
              substitui apenas produtos vinculados, custos, status e fornecedor.
              Foto, observação e anexos não são alterados.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-1">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-gray-500">
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Carregando histórico...
              </div>
            ) : snapshots.length === 0 ? (
              <div className="text-center py-12 text-gray-500 text-sm">
                Nenhuma versão registrada ainda. Snapshots começam a ser
                gravados automaticamente a cada save.
              </div>
            ) : (
              <ul className="divide-y border rounded-md">
                {snapshots.map((snap) => {
                  const motivo = motivoLabel[snap.motivo] || {
                    label: snap.motivo || 'save',
                    color: 'bg-gray-100 text-gray-700',
                  };
                  return (
                    <li
                      key={snap.id}
                      className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors gap-3"
                      data-testid={`snapshot-row-${snap.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm text-gray-900">
                            {formatDateBR(snap.created_at)}
                          </span>
                          <Badge className={`text-xs ${motivo.color}`} variant="secondary">
                            {motivo.label}
                          </Badge>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 truncate">
                          {snap.created_by_nome || snap.created_by_email}
                        </div>
                        <div className="text-xs text-gray-600 mt-1 flex items-center gap-3 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            {snap.total_itens || 0} itens
                          </span>
                          <span className="font-medium text-green-700">
                            {formatCurrency(snap.total_geral || 0)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setVerSnapshot(snap)}
                          data-testid={`snapshot-ver-${snap.id}`}
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          Ver
                        </Button>
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          className="bg-amber-600 hover:bg-amber-700"
                          onClick={() => setSnapshotParaRestaurar(snap)}
                          data-testid={`snapshot-restaurar-${snap.id}`}
                        >
                          <RotateCcw className="h-3.5 w-3.5 mr-1" />
                          Restaurar
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onClose?.()}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Ver snapshot (read-only) */}
      <Dialog open={!!verSnapshot} onOpenChange={(o) => !o && setVerSnapshot(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Visualizando versão de {verSnapshot && formatDateBR(verSnapshot.created_at)}
            </DialogTitle>
            <DialogDescription>
              {verSnapshot?.total_itens || 0} itens · Total{' '}
              <span className="font-semibold text-green-700">
                {formatCurrency(verSnapshot?.total_geral || 0)}
              </span>{' '}
              · por {verSnapshot?.created_by_nome || verSnapshot?.created_by_email}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            {itensVerSnapshot.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">
                Snapshot sem itens.
              </p>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-left">
                    <th className="px-2 py-2 font-medium text-gray-600 w-10">#</th>
                    <th className="px-2 py-2 font-medium text-gray-600">Item</th>
                    <th className="px-2 py-2 font-medium text-gray-600 w-20 text-center">
                      Produtos
                    </th>
                    <th className="px-2 py-2 font-medium text-gray-600 w-28 text-right">
                      Custo
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {itensVerSnapshot.map((it) => {
                    const produtos = Array.isArray(it.itens_custo) ? it.itens_custo : [];
                    let total = parseFloat(it.custo_manual || 0) || 0;
                    produtos.forEach((p) => {
                      total += parseFloat(p?.valor_total || 0) || 0;
                    });
                    return (
                      <tr key={`${it.id}-${it.numero}`} className="hover:bg-gray-50">
                        <td className="px-2 py-2 text-gray-500">{it.numero}</td>
                        <td className="px-2 py-2">
                          <div className="font-medium text-gray-900 truncate max-w-md">
                            {it.descricao}
                          </div>
                          <div className="text-[10px] text-gray-500">
                            {it.categoria || 'Geral'} · {it.tipo_item || 'especificacao'}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center text-gray-600">
                          {produtos.length}
                        </td>
                        <td className="px-2 py-2 text-right font-medium text-green-700">
                          {formatCurrency(total)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setVerSnapshot(null)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação de restauração */}
      <AlertDialog
        open={!!snapshotParaRestaurar}
        onOpenChange={(o) => !o && !restaurando && setSnapshotParaRestaurar(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurar esta versão?</AlertDialogTitle>
            <AlertDialogDescription>
              Os produtos vinculados, custos, status e fornecedores dos itens serão
              substituídos pelos da versão de{' '}
              <strong>
                {snapshotParaRestaurar &&
                  formatDateBR(snapshotParaRestaurar.created_at)}
              </strong>
              . Foto, observação e anexos não são alterados.
              <br />
              <br />
              Antes de restaurar, criamos automaticamente um snapshot de
              segurança do estado atual (você poderá voltar atrás).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restaurando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestaurar}
              disabled={restaurando}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirmar-restauracao"
            >
              {restaurando ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  Restaurando...
                </>
              ) : (
                'Restaurar versão'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default HistoricoFichaDialog;
